import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { salesLeadsApi } from './sales-leads'
import { queryKeys } from './query-keys'
import type {
  ApiSalesLead,
  AssignSalesManagerRequest,
  AssignSalesRepRequest,
  ChangeDepartmentRequest,
  SearchSalesLeadRequest,
} from './types'

const enabledTeam = (teamId: number | null | undefined) =>
  teamId != null && Number.isFinite(Number(teamId))

/** GET /v1/sales/:teamId/leads — paginated; default page 1, page size 50 (BE-fixed). */
export function useSalesLeads(teamId: number | null | undefined, page: number = 1) {
  return useQuery({
    queryKey: queryKeys.salesLeads.list(teamId ?? 'none', page),
    queryFn: () => salesLeadsApi.list(Number(teamId), page),
    enabled: enabledTeam(teamId),
  })
}

/** GET /v1/sales/:teamId/leads/pending — role-aware on the BE:
 *    GM-like → leads with no salesManager
 *    SM      → leads with no salesRep
 *    SR      → [] (BE returns empty; SR view should fall back to GET /leads filtered client-side) */
export function useSalesLeadsPending(teamId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.salesLeads.pending(teamId ?? 'none'),
    queryFn: () => salesLeadsApi.pending(Number(teamId)),
    enabled: enabledTeam(teamId),
  })
}

/** GET /v1/sales/:teamId/leads/assigned — role-aware mirror of pending. */
export function useSalesLeadsAssigned(teamId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.salesLeads.assigned(teamId ?? 'none'),
    queryFn: () => salesLeadsApi.assigned(Number(teamId)),
    enabled: enabledTeam(teamId),
  })
}

/** GET /v1/sales/:teamId/leads/:leadId — populates dealership/SM/SR/estimate. */
export function useSalesLead(
  teamId: number | null | undefined,
  leadId: number | null | undefined,
) {
  return useQuery({
    queryKey: queryKeys.salesLeads.detail(teamId ?? 'none', leadId ?? 'none'),
    queryFn: () => salesLeadsApi.get(Number(teamId), Number(leadId)),
    enabled: enabledTeam(teamId) && leadId != null,
  })
}

/** POST /v1/sales/:teamId/leads/search — server-side full search across the
 *  team's lead set. Empty string is short-circuited to skip the request and
 *  return [] so callers can switch between client filter and server search
 *  without flicker. */
export function useSalesLeadsSearch(
  teamId: number | null | undefined,
  searchTerm: string,
) {
  const trimmed = searchTerm.trim()
  return useQuery({
    queryKey: queryKeys.salesLeads.search(teamId ?? 'none', trimmed),
    queryFn: (): Promise<ApiSalesLead[]> =>
      trimmed === ''
        ? Promise.resolve([])
        : salesLeadsApi.search(Number(teamId), { searchTerm: trimmed } satisfies SearchSalesLeadRequest),
    enabled: enabledTeam(teamId) && trimmed !== '',
  })
}

const invalidateSalesLeadsScope = (
  qc: ReturnType<typeof useQueryClient>,
  teamId: number,
) => {
  qc.invalidateQueries({ queryKey: queryKeys.salesLeads.pending(teamId) })
  qc.invalidateQueries({ queryKey: queryKeys.salesLeads.assigned(teamId) })
  qc.invalidateQueries({ queryKey: queryKeys.salesLeads.all })
}

/** PATCH /v1/sales/:teamId/leads/:leadId/department. */
export function useChangeLeadDepartment(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, data }: { leadId: number; data: ChangeDepartmentRequest }) =>
      salesLeadsApi.changeDepartment(Number(teamId), leadId, data),
    onSuccess: () => {
      if (teamId != null) invalidateSalesLeadsScope(qc, Number(teamId))
    },
  })
}

/** POST /v1/sales/:teamId/leads/:leadId/sales-manager — dealership GM only. */
export function useAssignSalesManager(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, data }: { leadId: number; data: AssignSalesManagerRequest }) =>
      salesLeadsApi.assignSalesManager(Number(teamId), leadId, data),
    onSuccess: () => {
      if (teamId != null) invalidateSalesLeadsScope(qc, Number(teamId))
    },
  })
}

/** POST /v1/sales/:teamId/leads/:leadId/sales-rep — dealership SM only. */
export function useAssignSalesRep(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, data }: { leadId: number; data: AssignSalesRepRequest }) =>
      salesLeadsApi.assignSalesRep(Number(teamId), leadId, data),
    onSuccess: () => {
      if (teamId != null) invalidateSalesLeadsScope(qc, Number(teamId))
    },
  })
}
